import { Grid, Box, Typography, IconButton, styled, Slider, CircularProgress } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import { getWaveBlob } from 'webm-to-wav-converter';
import StyledTypography from '../../Common/StyledTypography';
import { SendButton } from '../Messenger/SendMessage';
import { useAppSelector } from '../../../redux/hooks';
import { SenderType, sendMessage } from '../../../redux/chat/chatSlice';
import { AssetType } from '../../../redux/activity/activitySlice';
import { toastStyles } from '../../../utils/utils';
import { formatTime } from '../../../utils/dateUtils';

const mimeType = 'audio/webm';

interface IPrettoSliderProps {
  isplaying: boolean;
}

export const PrettoSlider = styled(Slider)<IPrettoSliderProps>(({ isplaying }) => ({
  color: '#6599A5',
  height: 3,
  '& .MuiSlider-track': {
    border: 'none',
  },
  '& .MuiSlider-thumb': {
    display: isplaying ? 'block' : 'none',
    height: 16,
    width: 16,
    backgroundColor: '#fff',
    border: '4px solid currentColor',
    '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
      boxShadow: 'inherit',
    },
    '&::before': {
      display: 'none',
    },
  },
  '& .MuiSlider-valueLabel': {
    display: 'none',
  },
}));

interface ICaptureAudioProps {
  hide: (_isRecording: boolean) => void;
  clientId: string;
}

function CaptureAudio({ hide, clientId }: ICaptureAudioProps) {
  const coachId = useAppSelector((state) => state.coach.coach?.id);
  const dispatch = useDispatch();
  const [isRecording, setIsRecording] = useState(true);
  const [recordedAudio, setRecordedAudio] = useState<HTMLAudioElement | null>(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [currentPlaybackTime, setCurrentPlaybackTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [renderedAudio, setRenderedAudio] = useState<File | null>(null);
  const [isConverting, setIsConverting] = useState(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);

  useEffect(() => {
    let interval;
    if (isRecording) {
      interval = setInterval(() => {
        setRecordingDuration((prevDuration) => {
          setTotalDuration(prevDuration + 1);
          return prevDuration + 1;
        });
      }, 1000);
    }

    return () => {
      clearInterval(interval);
    };
  }, [isRecording]);

  useEffect(() => {
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        const mediaRecorder = new MediaRecorder(stream, { mimeType });
        mediaRecorderRef.current = mediaRecorder;
        if (audioRef.current) {
          audioRef.current.srcObject = stream;
        }

        const chunks: Blob[] = [];
        mediaRecorder.ondataavailable = (e) => chunks.push(e.data);
        mediaRecorder.onstop = () => {
          const blob = new Blob(chunks, { type: mimeType });
          const audioURL = URL.createObjectURL(blob);
          const audio = new Audio(audioURL);
          setRecordedAudio(audio);
        };
        mediaRecorder.start();
      })
      .catch((error: unknown) => {
        if (error instanceof Error) {
          toast.error(error.message, toastStyles.error);
        }
      });
  }, []);

  useEffect(() => {
    if (recordedAudio) {
      const updatePlaybackTime = () => {
        setCurrentPlaybackTime(recordedAudio.currentTime);
      };
      recordedAudio.addEventListener('timeupdate', updatePlaybackTime);
      return () => {
        recordedAudio.removeEventListener('timeupdate', updatePlaybackTime);
      };
    }
  }, [recordedAudio]);

  const handlePlayRecording = () => {
    if (recordedAudio) {
      recordedAudio.play();
      setIsPlaying(true);
    }
  };

  const handleStopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsConverting(true);

      const audioChunks: Blob[] = [];
      mediaRecorderRef.current.addEventListener('dataavailable', (event) => {
        audioChunks.push(event.data);
      });

      mediaRecorderRef.current.addEventListener('stop', async () => {
        try {
          const audioBlob = new Blob(audioChunks, { type: mimeType });
          const waveBlob = await getWaveBlob(audioBlob, false, { sampleRate: 16000 });
          const audioFile = new File([waveBlob], `${Date.now().toString()}.wav`, {
            type: 'audio/wav',
          });
          setRenderedAudio(audioFile);
        } finally {
          setIsConverting(false);
        }
      });
    }
  };

  const handlePauseRecording = () => {
    recordedAudio?.pause();
    setIsPlaying(false);
  };

  const handleSend = () => {
    if (!coachId || !clientId || !renderedAudio) return;
    const newMessage = {
      clientId,
      coachId,
      message: renderedAudio.name,
      senderType: SenderType.COACH,
      chatMessageType: AssetType.AUDIO,
      assetFile: renderedAudio,
      assetDuration: totalDuration.toString(),
    };
    dispatch(sendMessage(newMessage));
    hide(false);
  };

  const handleDeleteRecording = () => {
    hide(false);
    setIsRecording(false);
    setRecordedAudio(null);
    setRecordingDuration(0);
    setCurrentPlaybackTime(0);
    setTotalDuration(0);
    setIsPlaying(false);
    setRenderedAudio(null);
    audioRef.current = null;
    mediaRecorderRef.current = null;
  };

  return (
    <Grid display="flex" flexDirection="row" gap={2} width="100%">
      <Grid
        alignItems="center"
        height="48px"
        p="12px"
        border="1px solid var(--Accent-Unbleached-Silk, #FFDDC8)"
        borderRadius="10px"
        width="100%"
        display="flex"
      >
        {isRecording && (
          <StyledTypography
            variant="bodyBold"
            align="center"
            width="calc(100% - 32px)"
            justifyContent="center"
            display="flex"
          >
            <span>{formatTime(recordingDuration)}</span>
          </StyledTypography>
        )}
        {recordedAudio && !isPlaying && (
          <Grid gap={2} display="flex" flexDirection="row" alignItems="center" width="100%">
            <Box onClick={handlePlayRecording} display="flex" alignItems="center">
              <img
                src="/assets/icons/play-circle.svg"
                alt="start-playing"
                style={{ cursor: 'pointer' }}
              />
            </Box>
            <PrettoSlider
              valueLabelDisplay="auto"
              aria-label="pretto slider"
              min={0}
              max={totalDuration}
              value={currentPlaybackTime}
              isplaying={currentPlaybackTime !== 0}
            />
            <Typography variant="bodyRegular" color="primary">
              {formatTime(totalDuration)}
            </Typography>
            <Box display="flex" alignItems="center" onClick={handleDeleteRecording}>
              <img
                src="/assets/icons/close-circle-large.svg"
                alt="delete-recording"
                style={{ cursor: 'pointer' }}
              />
            </Box>
          </Grid>
        )}
        {recordedAudio && isPlaying && (
          <Grid gap={2} display="flex" flexDirection="row" alignItems="center" width="100%">
            {formatTime(currentPlaybackTime) < formatTime(totalDuration) ? (
              <Box display="flex" alignItems="center" onClick={handlePauseRecording}>
                <img
                  src="/assets/icons/pause-circle.svg"
                  alt="pause-playing"
                  style={{ cursor: 'pointer' }}
                />
              </Box>
            ) : (
              <Box onClick={handlePlayRecording} display="flex" alignItems="center">
                <img
                  src="/assets/icons/play-circle.svg"
                  alt="start-playing"
                  style={{ cursor: 'pointer' }}
                />
              </Box>
            )}
            <PrettoSlider
              valueLabelDisplay="auto"
              aria-label="pretto slider"
              min={0}
              max={totalDuration}
              value={currentPlaybackTime}
              isplaying
            />
            <Typography variant="bodyRegular" color="primary">
              {formatTime(currentPlaybackTime)}
            </Typography>
            <Box display="flex" alignItems="center" onClick={handleDeleteRecording}>
              <img
                src="/assets/icons/close-circle-large.svg"
                alt="delete-recording"
                style={{ cursor: 'pointer' }}
              />
            </Box>
          </Grid>
        )}
        <audio ref={audioRef} hidden>
          <track kind="captions" />
        </audio>
        <Grid>
          {isRecording && (
            <Box display="flex" alignItems="center" onClick={handleStopRecording}>
              <img
                src="/assets/icons/stop-circle.svg"
                alt="stop-recording"
                style={{ cursor: 'pointer' }}
                width={32}
                height={32}
              />
            </Box>
          )}
        </Grid>
      </Grid>
      {!isRecording && isConverting && (
        <Grid display="flex" flexDirection="row" alignItems="center">
          <CircularProgress variant="indeterminate" disableShrink size={24} />
        </Grid>
      )}
      {!isRecording && !isConverting && (
        <SendButton>
          <IconButton onClick={handleSend}>
            <img src="/assets/icons/plain.svg" alt="plain" />
          </IconButton>
        </SendButton>
      )}
    </Grid>
  );
}

export default CaptureAudio;
